import { useCallback, useMemo } from 'react';
import { TimeRangeComparison } from '@fullstory/proto/fs/services/search-settings/visualizationsettings';
import { Display } from '@fullstory/proto/fs/services/webber/frontend/display';
import { getPercentageChange } from '@fullstory/util/fmt';
import { TimeRange } from '@fullstory/proto/fs/api/segment';
import {
  changePercentageDisplayToFormatNumberOptions,
  determineDurationUnit,
  formatNumber,
  getCardTooltipValue,
  getCompareText,
  getDateDisplayString,
  toDateRangeComparison,
} from '../utils';
import { intl } from '@fullstory/intl';
import { FormatNumberOptions } from 'react-intl';

interface UseSingleNumberComparisonProps {
  value: number;
  pastValue?: number;
  timeRange?: TimeRange | null;
  pastRange?: TimeRangeComparison | null;
  displayFormat?: Display;
  customFormatter?: (value: number | null) => string;
  /**
   * If set to true, returns the absolute value of the percentage change.
   */
  abs?: boolean;
  formatNumberOptions?: Omit<FormatNumberOptions, 'style'>;
}

export const useSingleNumberComparison = ({
  value,
  pastValue,
  timeRange,
  pastRange,
  displayFormat,
  customFormatter,
  abs = true,
  formatNumberOptions,
}: UseSingleNumberComparisonProps) => {
  const start = timeRange?.startTime ?? '';
  const end = timeRange?.endTime ?? '';

  const now = useMemo(() => new Date(), []);
  const dateRangeComparison = toDateRangeComparison(pastRange ?? null, now);
  const previousRange = dateRangeComparison
    ? getCompareText(dateRangeComparison, true)
    : intl.formatMessage({ defaultMessage: 'Previous period', id: 'hCCWuZ' });

  const currentRange = getDateDisplayString(start, end);

  const durationUnit = useMemo(() => {
    if (
      !customFormatter &&
      displayFormat?.displayDuration &&
      pastValue &&
      !displayFormat.displayDuration.autoscale
    ) {
      return determineDurationUnit([value, pastValue], displayFormat);
    }

    return undefined;
  }, [customFormatter, displayFormat, value, pastValue]);

  const resolvedNumberFormatOptions = useMemo(() => {
    if (!displayFormat) return formatNumberOptions;

    return {
      // "defaults"
      ...changePercentageDisplayToFormatNumberOptions(displayFormat),
      // explicitly provided options (should override defaults)
      ...formatNumberOptions,
    };
  }, [formatNumberOptions, displayFormat]);

  const [comparisonText, comparisonValue] = getPercentageChange(
    value,
    pastValue ?? 0,
    abs,
    resolvedNumberFormatOptions,
  );

  const valueFormatter = useCallback(
    (value: number) => {
      if (customFormatter) {
        return customFormatter(value);
      } else if (displayFormat) {
        return formatNumber(value, displayFormat, durationUnit);
      }
      // legacy cards might not have a displayFormat
      return getCardTooltipValue(value);
    },
    [customFormatter, displayFormat, durationUnit],
  );

  const valueText = valueFormatter(value);
  const pastValueText = valueFormatter(pastValue ?? 0);

  return {
    previousRange,
    currentRange,
    comparisonText,
    comparisonValue,
    valueText,
    pastValueText,
  };
};
